import { NETWORK } from "@/lib/constant";
import type { NetworkEdge, NodeBlueprint } from "@/lib/types";

// Deterministic PRNG so a given member always lays out identically (stable across
// reloads and SSR→client), while still looking hand-scattered.
export const mulberry32 = (seed: number) => {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

export const hashString = (str: string) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const lerp = (a: number, b: number, r: number) => a + (b - a) * r;
const TAU = Math.PI * 2;

// Smooth in-out used for the entrance.
export const easeInOut = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

export const clamp = (v: number, min: number, max: number) =>
  v < min ? min : v > max ? max : v;

/**
 * Distribute nodes evenly over a unit sphere with the Fibonacci spiral (no poles
 * clumping). The stack alone is too sparse for a real molecule look, so we pad to
 * a denser count with plain structural dots and spread the labelled tech nodes
 * across the shell. Every node gets its own drift phase so the surface breathes.
 */
export const buildSphere = (
  techStack: string[],
  seed: number,
): NodeBlueprint[] => {
  const rand = mulberry32(seed);
  const labelled = techStack.length;
  // Pad to a denser mesh, but never fewer nodes than there are real techs — so
  // growing a member's stack always just shows more labels, none ever dropped.
  const total = Math.max(
    clamp(labelled * NETWORK.fillFactor, NETWORK.fillMin, NETWORK.fillMax),
    labelled,
  );
  const GOLDEN = Math.PI * (3 - Math.sqrt(5));
  // The leading techs (listed first in the data) are the important ones.
  const importantCount = Math.ceil(labelled * NETWORK.importantRatio);

  // Assign the real tech labels to indices spread across the whole spiral.
  const labelAt = new Map<number, string>();
  const importantAt = new Set<number>();
  for (let k = 0; k < labelled; k++) {
    const idx = Math.floor(((k + 0.5) * total) / labelled);
    labelAt.set(idx, techStack[k]);
    if (k < importantCount) importantAt.add(idx);
  }

  return Array.from({ length: total }, (_, i) => {
    const y = 1 - (i / (total - 1)) * 2; // 1 → -1
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = i * GOLDEN;
    const name = labelAt.get(i) ?? "";

    return {
      name,
      label: labelAt.has(i),
      important: importantAt.has(i),
      bx: Math.cos(theta) * r,
      by: y,
      bz: Math.sin(theta) * r,
      amp: lerp(NETWORK.driftMin, NETWORK.driftMax, rand()),
      f1: lerp(0.2, 0.6, rand()),
      f2: lerp(0.1, 0.3, rand()),
      p1: rand() * TAU,
      p2: rand() * TAU,
      delay: NETWORK.entranceStart + (i / total) * NETWORK.entranceSpread,
    };
  });
};

/**
 * Wire each node to its nearest neighbours on the sphere (largest dot product =
 * smallest arc). Topology is fixed once — the shell rotates rigidly, so neighbours
 * stay neighbours and the triangulated mesh holds together.
 */
export const buildSphereEdges = (nodes: NodeBlueprint[]): NetworkEdge[] => {
  const n = nodes.length;
  const edges: NetworkEdge[] = [];
  const seen = new Set<string>();
  const add = (a: number, b: number) => {
    if (a === b) return;
    const key = a < b ? `${a}:${b}` : `${b}:${a}`;
    if (seen.has(key)) return;
    seen.add(key);
    edges.push({ a, b });
  };

  for (let i = 0; i < n; i++) {
    const near = [];
    for (let j = 0; j < n; j++) {
      if (i === j) continue;
      const dot = nodes[i].bx * nodes[j].bx + nodes[i].by * nodes[j].by + nodes[i].bz * nodes[j].bz;
      near.push({ j, dot });
    }
    near.sort((p, q) => q.dot - p.dot);
    for (let k = 0; k < Math.min(NETWORK.neighborLinks, near.length); k++) {
      add(i, near[k].j);
    }
  }

  return edges;
};
